import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { levelFor } from "../lib/levels";
import { toast } from "sonner";
import { Check } from "lucide-react";

const CYCLE = [
  { label: "Inhale", seconds: 4 },
  { label: "Hold", seconds: 4 },
  { label: "Exhale", seconds: 6 },
  { label: "Hold", seconds: 2 },
];

export default function Breathing() {
  const { user, setUser } = useAuth();
  const lvl = levelFor(user?.current_level || 1);
  const [running, setRunning] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [remaining, setRemaining] = useState(CYCLE[0].seconds);
  const [cycles, setCycles] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!running) return;
    const tick = setInterval(() => {
      setRemaining(r => {
        if (r > 1) return r - 1;
        setPhaseIdx(p => {
          const next = (p + 1) % CYCLE.length;
          if (next === 0) setCycles(c => c + 1);
          return next;
        });
        // remaining for the new phase will be set by the phaseIdx effect below
        return 0;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [running]);

  // when phase changes, seed the seconds for that phase
  useEffect(() => {
    setRemaining(CYCLE[phaseIdx].seconds);
  }, [phaseIdx]);

  useEffect(() => {
    if (cycles >= 4 && running) {
      setRunning(false);
      complete();
    }
  }, [cycles]);

  const complete = async () => {
    try {
      const { data } = await api.post("/exercise/breathing/complete");
      if (data.user) setUser(data.user);
      setDone(true);
      toast.success(data.awarded ? `+15 XP · Stillness +1` : "Nice. Already counted today.");
    } catch { toast.error("Couldn't save your rep."); }
  };

  const phase = CYCLE[phaseIdx];

  return (
    <div className="animate-fadeUp" data-testid="breathing-page">
      <div className="text-xs font-bold uppercase tracking-[0.25em] opacity-60">Exercise</div>
      <h1 className="font-display font-black text-4xl tracking-tighter mt-2">Box breathing</h1>
      <p className="opacity-70 mt-2 max-w-xl">Four cycles. In four, hold four, out six, hold two. Soft, slow, no performance.</p>

      <div className="mt-10 flex flex-col items-center justify-center min-h-[55vh] relative">
        <div
          className="rounded-full bg-white shadow-2xl flex items-center justify-center"
          style={{
            width: 280, height: 280,
            transform: `scale(${running ? (phase.label === "Inhale" ? 1.25 : phase.label === "Exhale" ? 0.9 : 1.1) : 1})`,
            transition: `transform ${running ? phase.seconds : 0.6}s ease-in-out`,
          }}
          data-testid="breathing-circle"
        >
          <div className="text-center">
            <div className="font-display font-black text-4xl tracking-tighter">{running ? phase.label : "Ready?"}</div>
            <div className="text-5xl font-display font-black mt-2" style={{ color: lvl.accent }}>{running ? remaining : "—"}</div>
            <div className="text-xs uppercase tracking-widest opacity-60 mt-2">Cycle {cycles + 1} / 4</div>
          </div>
        </div>

        <div className="mt-10 flex gap-3">
          {!done && (
            <button data-testid="breathing-toggle" onClick={() => setRunning(r => !r)} className="btn-pill bg-[#0e203b] text-white rounded-full px-7 h-14 font-bold">
              {running ? "Pause" : cycles > 0 ? "Resume" : "Start"}
            </button>
          )}
          {done && (
            <div className="flex items-center gap-2 bg-white rounded-full px-5 h-12 font-bold border-2 border-[#0e203b]/10" data-testid="breathing-done">
              <Check size={16} /> Rep complete
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
